import { z } from 'zod'
import type { GuestDraft } from '../guestDrafts'

const PREFIX = 'buffer:publish:public-handoff:'
const HandoffTracking = z.object({
  publicSessionId: z.string(),
  draftCount: z.number().int().nonnegative(),
  hasText: z.boolean(),
  expiredMediaIds: z.array(z.string()),
  hasUnavailableMedia: z.boolean(),
})
export type HandoffTracking = z.infer<typeof HandoffTracking>

export function saveHandoffTracking(
  intentId: string,
  tracking: HandoffTracking,
): void {
  try {
    sessionStorage.setItem(`${PREFIX}${intentId}`, JSON.stringify(tracking))
  } catch {
    /* Import must work without analytics storage. */
  }
}

export function clearHandoffTracking(intentId: string): void {
  try {
    sessionStorage.removeItem(`${PREFIX}${intentId}`)
  } catch {
    /* Storage may be unavailable. */
  }
}

export function startHandoffTracking({
  intentId,
  publicSessionId,
  drafts,
  importedDraftIds,
  now,
}: {
  intentId: string
  publicSessionId: string | undefined
  drafts: readonly GuestDraft[]
  importedDraftIds: readonly string[]
  now: number
}): HandoffTracking | undefined {
  if (!publicSessionId) {
    clearHandoffTracking(intentId)
    return undefined
  }
  let previous: HandoffTracking | undefined
  try {
    const parsed = HandoffTracking.safeParse(
      JSON.parse(sessionStorage.getItem(`${PREFIX}${intentId}`) ?? 'null'),
    )
    if (parsed.success && parsed.data.publicSessionId === publicSessionId)
      previous = parsed.data
  } catch {
    /* Start with the current product snapshot. */
  }
  const tracking = {
    publicSessionId,
    draftCount: Math.max(
      previous?.draftCount ?? 0,
      new Set([...importedDraftIds, ...drafts.map((draft) => draft.id)]).size,
    ),
    hasText:
      !!previous?.hasText ||
      drafts.some((draft) => draft.text.trim().length > 0),
    expiredMediaIds: [
      ...new Set([
        ...(previous?.expiredMediaIds ?? []),
        ...drafts.flatMap((draft) =>
          draft.media
            .filter((media) => media.expiresAt <= now)
            .map((media) => media.id),
        ),
      ]),
    ],
    hasUnavailableMedia: previous?.hasUnavailableMedia ?? false,
  }
  saveHandoffTracking(intentId, tracking)
  return tracking
}

export function clearAllHandoffTracking(): void {
  try {
    for (const key of Object.keys(sessionStorage)) {
      if (key.startsWith(PREFIX)) sessionStorage.removeItem(key)
    }
  } catch {
    /* Storage may be unavailable. */
  }
}
